// Attack support calculations (SF-55; trimmed SF-147 — the Calculator-specific formulas this
// file used to hold moved with AttackCalculatorTab.tsx's retirement). What remains is shared
// infrastructure still consumed outside the retired Calculator tab: travel time (War Room +
// AmbushLaunchPanel) and intel-freshness display helpers (IntelFreshnessIndicator.tsx).

import type { Province } from '../types/province';
import type { TravelTimeBreakdown } from '../types/attackCalculator';

// --- Travel Time ---

const ATTACK_TIME_INTRA_KINGDOM = 12.6; // ticks (same kingdom)
const ATTACK_TIME_SAME_ISLAND = 14.4; // ticks (different kingdom, same island)
const ATTACK_TIME_DIFF_ISLAND = 16.0; // ticks (different island)

function getBaseAttackTime(
  province: Province,
  targetIsland?: number,
  targetKingdom?: number
): number {
  if (targetIsland === undefined || targetKingdom === undefined) {
    return ATTACK_TIME_DIFF_ISLAND;
  }
  const { island, kingdom } = province.metadata.location;
  if (island === targetIsland && kingdom === targetKingdom) {
    return ATTACK_TIME_INTRA_KINGDOM;
  }
  if (island === targetIsland) {
    return ATTACK_TIME_SAME_ISLAND;
  }
  return ATTACK_TIME_DIFF_ISLAND;
}

export function calculateTravelTime(
  province: Province,
  isAtWar: boolean,
  targetIsland?: number,
  targetKingdom?: number
): TravelTimeBreakdown {
  const baseTime = getBaseAttackTime(province, targetIsland, targetKingdom);

  const barracksPercent = getBuildingPercent(province, 'MILITARY_BARRACK');
  const barracksModifier = 1 - barracksPercent;

  const hasQuickFeet = hasEffect(province.magic.activeEffects, 'QUICK_FEET');
  const quickFeetModifier = hasQuickFeet ? 0.75 : 1.0;

  const warModifier = isAtWar ? 0.80 : 1.0;

  const finalTime = Math.max(1, Math.round(
    baseTime * barracksModifier * quickFeetModifier * warModifier
  ));

  return {
    baseTime,
    barracksModifier,
    quickFeetModifier,
    warModifier,
    finalTime,
  };
}

// --- Intel Freshness ---

export function getIntelFreshnessColor(capturedAt: string): string {
  const ageMs = Date.now() - new Date(capturedAt).getTime();
  const ageHours = ageMs / (1000 * 60 * 60);

  if (ageHours < 1) return 'text-green-400';
  if (ageHours < 5) return 'text-yellow-400';
  if (ageHours < 24) return 'text-orange-400';
  return 'text-red-400';
}

export function getIntelFreshnessDotColor(capturedAt: string): string {
  const ageMs = Date.now() - new Date(capturedAt).getTime();
  const ageHours = ageMs / (1000 * 60 * 60);

  if (ageHours < 1) return 'bg-green-400';
  if (ageHours < 5) return 'bg-yellow-400';
  if (ageHours < 24) return 'bg-orange-400';
  return 'bg-red-400';
}

export function getIntelAge(capturedAt: string): string {
  const ageMs = Date.now() - new Date(capturedAt).getTime();
  const ageMinutes = Math.floor(ageMs / (1000 * 60));

  if (ageMinutes < 60) return `${ageMinutes}m ago`;
  const ageHours = Math.floor(ageMinutes / 60);
  if (ageHours < 24) return `${ageHours}h ago`;
  const ageDays = Math.floor(ageHours / 24);
  return `${ageDays}d ago`;
}

// --- Helper Functions ---

function getBuildingPercent(province: Province, buildingType: string): number {
  const totalBuildings = province.territory.totals.buildings;
  if (totalBuildings === 0) return 0;
  const buildingCount = province.territory.buildings[buildingType] ?? 0;
  return buildingCount / totalBuildings;
}

function hasEffect(effects: { effectType: string }[], effectType: string): boolean {
  return effects.some((e) => e.effectType === effectType);
}
